import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  // Menu,
  // MenuItem,
  // ListItemIcon,
  // ListItemText,
  useMediaQuery,
  Paper,
  Chip,
} from "@mui/material";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import {
  ArrowBack,
  // PlaylistPlay,
  School,
  AccountCircle,
  ExitToApp,
  // VideoLibrary,
} from "@mui/icons-material";
import ReactPlayer from "react-player";
import { useAuth } from "../contexts/AuthContext";
import { getMovieById } from "../api/moviesApi";
import { checkSession, logout as apiLogout } from "../api/authApi";
import { replaceVideoSubdomain } from "../utils/videoUtils";
import Hls from "hls.js";

const MoviePlayer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const playerRef = useRef(null);
  const videoRef = useRef(null);

  const [movie, setMovie] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentEpisodeIndex, setCurrentEpisodeIndex] = useState(0);
  const [countdown, setCountdown] = useState(null);
  const [finished, setFinished] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [tokenExpired, setTokenExpired] = useState(false);
  // const [anchorEl, setAnchorEl] = useState(null);
  // const openMenu = Boolean(anchorEl);
  const { logout } = useAuth();

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const theme = createTheme({
    palette: {
      mode: "light",
      background: {
        default: "#F8F9FA",
        paper: "#FFFFFF",
      },
      primary: {
        main: "#1565C0",
      },
      secondary: {
        main: "#FF8F00",
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
  });

  const originalVideoSrc = movie?.episodes?.[currentEpisodeIndex]?.link;
  const videoSrc = replaceVideoSubdomain(originalVideoSrc);

  const fetchMovie = async () => {
    try {
      const data = await getMovieById(id);
      setMovie(data);
    } catch (err) {
      console.error("❌ Failed to fetch movie:", err);
      if (err?.response?.status === 401) {
        setTokenExpired(true);
      } else {
        navigate("/courses");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMovie();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (countdown === 0) {
      playNextEpisode();
    } else if (countdown !== null) {
      const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown]);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const valid = await checkSession();
        if (!valid) {
          setTokenExpired(true);
        }
      } catch (err) {
        console.error("🔐 Session check error:", err);
        setTokenExpired(true);
      }
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const playNextEpisode = async () => {
    try {
      const valid = await checkSession();
      if (!valid) {
        setTokenExpired(true);
        return;
      }
      if (currentEpisodeIndex + 1 < movie.episodes.length) {
        setCurrentEpisodeIndex((prev) => prev + 1);
        setIsPlaying(true);
        setShowCountdown(false);
        setCountdown(null);
      } else {
        setFinished(true);
      }
    } catch (error) {
      console.error("Error checking session before auto next episode:", error);
      setTokenExpired(true);
    }
  };

  // const handleChangeEpisode = async (index) => {
  //   try {
  //     const valid = await checkSession();
  //     if (!valid) {
  //       setTokenExpired(true);
  //       return;
  //     }
  //     setCurrentEpisodeIndex(index);
  //     setIsPlaying(false);
  //     setFinished(false);
  //     setCountdown(null);
  //     setShowCountdown(false);
  //     setAnchorEl(null);
  //   } catch (error) {
  //     console.error("Error checking session:", error);
  //   }
  // };

  const refreshVideo = () => {
    window.location.reload();
  };

  useEffect(() => {
    const isMobileUser = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isMobileUser || isSafari) return;

    const handleContextMenu = (e) => e.preventDefault();
    const handleKeyDown = (e) => {
      if (
        e.key === "F12" ||
        (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "i") ||
        (e.ctrlKey && e.key.toLowerCase() === "u") ||
        (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "j")
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const detectDevTools = () => {
      const threshold = 160;
      const widthThreshold = window.outerWidth - window.innerWidth > threshold;
      const heightThreshold =
        window.outerHeight - window.innerHeight > threshold;
      if (widthThreshold || heightThreshold) {
        alert("🚫 กรุณาปิด DevTools เพื่อใช้งานเว็บไซต์นี้");
        window.location.replace("/login");
      }
    };
    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);
    const interval = setInterval(detectDevTools, 1000);
    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
      clearInterval(interval);
    };
  }, []);

  // ✅ HLS setup รองรับ iPhone Safari
  useEffect(() => {
    if (!videoSrc || !videoSrc.endsWith(".m3u8") || !videoRef.current) return;

    const videoElement = videoRef.current;
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const canNativePlay = videoElement.canPlayType(
      "application/vnd.apple.mpegurl"
    );

    if (Hls.isSupported() && !isIOS) {
      const hls = new Hls();
      hls.loadSource(videoSrc);
      hls.attachMedia(videoElement);
      return () => {
        hls.destroy();
      };
    } else if (isIOS || canNativePlay) {
      videoElement.src = videoSrc;
      videoElement.setAttribute("playsinline", "true");
      videoElement.setAttribute("webkit-playsinline", "true");
      videoElement.setAttribute("muted", "true");
      videoElement.preload = "metadata";
      videoElement.load();

      // iOS ต้องการ user interaction ก่อน
      const playVideo = () => {
        videoElement.muted = false;
        videoElement.play().catch((err) => {
          console.warn("Autoplay blocked:", err);
        });
      };

      // ลองเล่นเมื่อมี user interaction
      document.addEventListener("touchstart", playVideo, { once: true });
      document.addEventListener("click", playVideo, { once: true });

      return () => {
        document.removeEventListener("touchstart", playVideo);
        document.removeEventListener("click", playVideo);
      };
    }
  }, [videoSrc]);

  if (loading || !movie) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          minHeight: "100vh",
        }}
      >
        {/* Academic Header */}
        <AppBar
          position="sticky"
          sx={{
            backgroundColor: "#FFFFFF",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <Toolbar
            sx={{
              display: "flex",
              justifyContent: "space-between",
              px: { xs: 1, sm: 2 },
            }}
          >
            <IconButton
              edge="start"
              sx={{ color: "#1565C0", mr: { xs: 1, sm: 2 } }}
              onClick={() => navigate("/courses", { replace: true })}
            >
              <ArrowBack />
            </IconButton>

            {/* Course Title Section */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexGrow: 1,
                ml: { xs: 1, sm: 2 },
                minWidth: 0, // Allow shrinking
                overflow: "hidden",
              }}
            >
              {!isMobile && (
                <School sx={{ mr: 2, color: "#1565C0", flexShrink: 0 }} />
              )}
              <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                <Typography
                  variant={isMobile ? "body1" : "h6"}
                  sx={{
                    fontWeight: 700,
                    color: "#1565C0",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: { xs: "150px", sm: "300px", md: "none" },
                  }}
                >
                  {movie?.title || "กำลังโหลด..."}
                </Typography>
                {!isMobile && (
                  <Typography variant="caption" sx={{ color: "#666" }}>
                    ห้องเรียนออนไลน์
                  </Typography>
                )}
              </Box>
            </Box>

            <Box
              sx={{ display: "flex", gap: { xs: 0.5, sm: 1 }, flexShrink: 0 }}
            >
              <Button
                startIcon={!isMobile ? <AccountCircle /> : undefined}
                onClick={() => navigate("/profile")}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#1565C0",
                  "&:hover": { backgroundColor: "#E3F2FD" },
                  minWidth: "auto",
                  px: { xs: 1, sm: 2 },
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                }}
              >
                {isMobile ? <AccountCircle /> : "โปรไฟล์"}
              </Button>
              <Button
                startIcon={!isMobile ? <ExitToApp /> : undefined}
                onClick={() => {
                  logout?.();
                  localStorage.clear();
                  navigate("/academic");
                }}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#D32F2F",
                  "&:hover": { backgroundColor: "#FFEBEE" },
                  minWidth: "auto",
                  px: { xs: 1, sm: 2 },
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                }}
              >
                {isMobile ? <ExitToApp /> : "ออกจากระบบ"}
              </Button>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Learning Video Player */}
        <Container
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 3,
            mb: 5,
          }}
        >
          <Paper
            onContextMenu={(e) => e.preventDefault()}
            sx={{
              position: "relative",
              borderRadius: 4,
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              backgroundColor: "#000",
              width: "100%",
              maxWidth: "1200px",
              border: "2px solid #E3F2FD",
            }}
          >
            {/* Episode/Chapter Menu - Hidden 
            <Box
              sx={{ position: "absolute", top: 12, right: 12, zIndex: 1300 }}
            >
              <Button
                variant="contained"
                size={isMobile ? "small" : "medium"}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setAnchorEl(e.currentTarget);
                }}
                startIcon={<PlaylistPlay />}
                sx={{
                  borderRadius: 3,
                  fontWeight: 600,
                  backgroundColor: "#1565C0",
                  "&:hover": { backgroundColor: "#0D47A1" },
                }}
              >
                บทที่ {movie.episodes[currentEpisodeIndex].episode}
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={openMenu}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                  sx: {
                    maxHeight: 300,
                    width: 300,
                    overflowY: "auto",
                    borderRadius: 2,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                  },
                }}
              >
                {movie.episodes.map((ep, index) => (
                  <MenuItem
                    key={index}
                    onClick={() => handleChangeEpisode(index)}
                    sx={{
                      py: 1.5,
                      "&:hover": { backgroundColor: "#E3F2FD" },
                    }}
                  >
                    <ListItemIcon>
                      <VideoLibrary
                        fontSize="small"
                        sx={{ color: "#1565C0" }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={`บทที่ ${ep.episode}: ${ep.title}`}
                      primaryTypographyProps={{
                        fontWeight: 500,
                        color: "#1565C0",
                      }}
                    />
                  </MenuItem>
                ))}
              </Menu>
            </Box>
            */}

            {/* Player */}
            {videoSrc?.endsWith(".m3u8") ? (
              <video
                ref={videoRef}
                controls
                playsInline
                webkit-playsinline="true"
                preload="metadata"
                muted
                crossOrigin="anonymous"
                onEnded={() => {
                  if (currentEpisodeIndex + 1 < movie.episodes.length) {
                    setCountdown(3);
                    setShowCountdown(true);
                  } else {
                    setFinished(true);
                  }
                }}
                onLoadedMetadata={() => {
                  // iOS: เมื่อโหลดข้อมูลเสร็จแล้ว unmute และพร้อมเล่น
                  if (videoRef.current) {
                    videoRef.current.muted = false;
                  }
                }}
                style={{
                  width: "100%",
                  height: "90vh",
                  backgroundColor: "#000",
                  borderRadius: "12px",
                }}
              />
            ) : (
              <ReactPlayer
                ref={playerRef}
                url={videoSrc}
                playing={isPlaying}
                controls
                width="100%"
                height="90vh"
                onEnded={() => {
                  if (currentEpisodeIndex + 1 < movie.episodes.length) {
                    setCountdown(3);
                    setShowCountdown(true);
                  } else {
                    setFinished(true);
                  }
                }}
                config={{
                  file: {
                    attributes: {
                      controlsList: "nodownload",
                      preload: "auto",
                      playsInline: true,
                      muted: false,
                      autoPlay: true,
                    },
                  },
                }}
                style={{ borderRadius: 12, backgroundColor: "#000" }}
              />
            )}

            {showCountdown && countdown !== null && (
              <Chip
                label={`บทถัดไปจะเล่นใน ${countdown} วินาที...`}
                sx={{
                  position: "absolute",
                  bottom: 16,
                  left: "50%",
                  transform: "translateX(-50%)",
                  backgroundColor: "rgba(21, 101, 192, 0.9)",
                  color: "white",
                  fontWeight: 600,
                  zIndex: 1500,
                  px: 2,
                  py: 1,
                }}
              />
            )}

            {finished && (
              <Paper
                sx={{
                  position: "absolute",
                  bottom: 16,
                  left: "50%",
                  transform: "translateX(-50%)",
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(10px)",
                  px: 4,
                  py: 3,
                  borderRadius: 3,
                  textAlign: "center",
                  zIndex: 1500,
                  border: "2px solid #1565C0",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", mb: 2, color: "#1565C0" }}
                >
                  � เรียนจบแล้ว
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: "#666" }}>
                  ขอแสดงความยินดี! คุณได้เรียนจบหลักสูตรนี้แล้ว
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate("/courses")}
                  sx={{
                    fontWeight: 600,
                    borderRadius: 2,
                    backgroundColor: "#1565C0",
                    "&:hover": { backgroundColor: "#0D47A1" },
                  }}
                >
                  กลับไปเรียนหลักสูตรอื่น
                </Button>
              </Paper>
            )}
          </Paper>
        </Container>

        <Box sx={{ textAlign: "center", mb: 5 }}>
          <Button
            variant="outlined"
            onClick={refreshVideo}
            sx={{
              borderColor: "#1565C0",
              color: "#1565C0",
              fontWeight: 600,
              borderRadius: 2,
              "&:hover": {
                backgroundColor: "#E3F2FD",
                borderColor: "#0D47A1",
              },
            }}
          >
            � รีเฟรชเนื้อหา
          </Button>

          {/* Video Server Info */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              backgroundColor: "#F5F5F5",
              borderRadius: 2,
              maxWidth: "400px",
              mx: "auto",
            }}
          >
            <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
              🎬 ข้อมูลวิดีโอ:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#1565C0",
                fontWeight: 600,
                textAlign: "center",
              }}
            >
              Server:{" "}
              {videoSrc ? new URL(videoSrc).hostname.split(".")[0] : "default"}
            </Typography>
          </Box>
        </Box>

        {/* Session Expired Dialog */}
        <Dialog
          open={tokenExpired}
          onClose={() => {}} // ป้องกันการปิด modal โดยการคลิกข้างนอก
          disableEscapeKeyDown // ป้องกันการปิด modal ด้วย ESC key
          PaperProps={{
            sx: {
              borderRadius: 3,
              padding: 2,
            },
          }}
        >
          <DialogTitle sx={{ textAlign: "center", color: "#1565C0" }}>
            <School sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h6" fontWeight="bold">
              หมดเวลาการเรียน
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ textAlign: "center", color: "#666" }}>
              เซสชันการเรียนของคุณหมดอายุแล้ว กรุณาเข้าสู่ระบบใหม่เพื่อเรียนต่อ
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pt: 2 }}>
            <Button
              onClick={() => {
                apiLogout();
                logout();
                localStorage.clear();
                navigate("/academic");
              }}
              variant="contained"
              sx={{
                backgroundColor: "#1565C0",
                "&:hover": { backgroundColor: "#0D47A1" },
                fontWeight: 600,
                borderRadius: 2,
              }}
            >
              เข้าสู่ระบบใหม่
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default MoviePlayer;
